import * as React from 'react'
import { castingI18nCastingType, castingWeeksInterval } from '../../../services/models/castings.service'
import { StoreProps, alwaysCompletedFocusedInputs, castingStoreGetSelectedOptionJoined, focusedInputs } from '../../../services/stores/casting.store'
import { I18nService } from '../../../services/utils/i18n.service'
import { formatDurationFromSecs } from '../../../services/utils/utils.service'
import { computePriceRows } from '../../plugins/casting/price/casting-price.component'
import ProgressBarComponent from '../../shared/progress-bar.component'
import { moment } from '../../../services/utils/moment.service'

// TODO: migrate subdependencies to typescript
import { getScrollHeight, getPosition } from '../../../../utils/dom'
import { theme } from '../../../theme'

interface State{
  headerFixed: boolean,
  headerHeight: string
}

const i18nScoped = new I18nService(require('./casting-header.component.yml'))

export default class CastingHeaderComponent extends React.Component<StoreProps & { mobile: boolean }, State> {
  state: State = { headerFixed: false, headerHeight: 'auto' }
  private headerRef = React.createRef<HTMLElement>()

  componentDidMount () {
    window.addEventListener('scroll', this.fixHeader.bind(this))
  }

  private fixHeader () {
    const header = this.headerRef.current
    const container = this.headerRef.current.parentNode

    if (this.state.headerFixed) {
      if (getScrollHeight() <= getPosition(container).top) {
        this.setState({ headerFixed: false, headerHeight: header.offsetHeight + 'px' })
      }
    } else {
      if (getScrollHeight() > getPosition(container).top) {
        this.setState({ headerFixed: true, headerHeight: header.offsetHeight + 'px' })
      }
    }
  }

  private renderPriceBlock () {
    const { store, data, data: { market, offeredService, selectedOptions } } = this.props
    const rows = computePriceRows({ store, data })
    const priceDependsOnSupplier = market.market_type === 'Marketplace' && (
      offeredService.marketplace_price_mode !== 'fixed' ||
      castingStoreGetSelectedOptionJoined({ offeredService, selectedOptions }).some(so => !so.offeredOption.marketplace_fixed_price)
    )

    const pricedEvents = store.getPricedEvents(true)

    // If multiple prices available OR if multiple segments to price, there is enough to display in the price information tooltip
    const complexPriceApplyed = !!pricedEvents?.length && (
      pricedEvents[0].first_reccurrence.priced_events.flatMap(_ => _.instances).length > 1 ||
      pricedEvents[0].first_reccurrence.displayedPriceCents !== pricedEvents[pricedEvents.length - 1].first_reccurrence.displayedPriceCents
    )

    const showHeader = !offeredService.casting_custom_price && (priceDependsOnSupplier || complexPriceApplyed)
    const style: React.CSSProperties = {
      cursor: complexPriceApplyed ? 'pointer' : 'unset',
      marginBottom: window.innerWidth > theme.breakpoints.m ? '0.5em' : 0,
      marginTop: window.innerWidth > theme.breakpoints.m && rows.length > 2 ? '-0.5em' : 0
    }

    return <div className="castingHeader-right">
      <div className="priceBlock" onClick={ () => showHeader && store.tryFocus('price') } style={ style }>
        { showHeader && <div className="priceBlock-line" style={{ marginTop: -5 }}>
          { priceDependsOnSupplier && <span className="priceBlock-text">{ i18nScoped.t('starting_at') }:</span> }
          { !priceDependsOnSupplier && <a className="priceBlock-text">{ i18nScoped.t('click_for_details') }:</a> }
        </div> }

        { rows.map((row, index) => <div key={ index } className="priceBlock-line" style={{ overflow: 'visible' }}>{ row }</div>) }
      </div>
    </div>
  }

  private getProgressPercent () {
    const focusableInputs = focusedInputs
      .filter(focusedInput => !alwaysCompletedFocusedInputs.includes(focusedInput as any))
      .filter(focusedInput => this.props.store.canFocus(focusedInput))

    return Math.round(focusableInputs.reduce((acc, curr) => acc + this.props.store.getProgressForStep(curr), 0) / Math.max(3, focusableInputs.length))
  }

  render () {
    const { props: { store, data: { castingType, city, offeredService, slots, quantity, selectedOptions } } } = this
    const duration = slots.reduce((acc, slot) => acc + slot.duration, 0)
    const startDate = slots.filter(slot => slot.start_date).map(slot => moment(slot.start_date)).sort((a, b) => a.valueOf() - b.valueOf())[0]
    const nbsp = String.fromCharCode(160)

    const recapWords: string[] = []
    if (castingType) recapWords.push(castingI18nCastingType(castingType))
    if (slots.length && slots[0].weeks_interval) recapWords.push(castingWeeksInterval(slots[0].weeks_interval))
    if (offeredService) recapWords.push(offeredService.name)
    if (city) recapWords.push(`${city.city}${(city.postalCode) ? (` (${city.postalCode})`) : ''}`)

    if (duration && !offeredService.calculator_hide_duration) {
      const formattedDuration = formatDurationFromSecs(duration).replace(/ /g, nbsp)

      if (slots[0]?.employees_count > 1) recapWords.push(i18nScoped.t('duration_mul_employees', { duration: formattedDuration, employees_count: slots[0].employees_count }))
      else recapWords.push(formattedDuration)
    }

    if (selectedOptions) recapWords.push(...store.getSelectedOptionJoined().map(so => store.formatSelectedOption(so, true).replace(/ /g, nbsp)))
    if (startDate) recapWords.push(startDate.format('LL').replace(/ /g, nbsp))
    if (quantity) recapWords.push(i18nScoped.t('quantity', { count: quantity }).replace(/ /g, nbsp))

    return <div className="castingHeader-container" style={{ height: this.state.headerHeight ? this.state.headerHeight : 'auto', display: recapWords.length ? 'block' : 'none' }}>
      <header className={ `castingHeader ${this.state.headerFixed ? 'is-fixed' : ''}` } ref={ this.headerRef }>
        <div className="containerPage-inner">
          <div className="castingHeader-split">
            { window.innerWidth > theme.breakpoints.m && <div className="castingHeader-left">
              <h2 className="castingHeader-title txtS">{ i18nScoped.t('overview') }</h2>

              <div className="listWordSep" style={{ maxHeight: '3em', overflow: 'hidden' }}>
                { recapWords.map((word, index) => `${word}${index !== recapWords.length - 1 ? ' | ' : '' }`).join('') }
              </div>
            </div> }

            { /* the castingHeader-right should be rendered, so castingHeader-left's width can be computed */}
            { store.canFocus('price') && offeredService['display_price_casting?'] ? this.renderPriceBlock() : <div className="castingHeader-right" style={{ width: 1 }}></div> }
          </div>

          <ProgressBarComponent value={ this.getProgressPercent() } max={ 100 }/>
        </div>
      </header>
    </div>
  }
}
